/**
 * OWL2 XML Parser
 *
 * Parses OWL2/XML format into TypeScript types.
 * Uses fast-xml-parser for XML parsing.
 */

import { XMLParser } from 'fast-xml-parser';
import type { Ontology, PrefixDeclaration, Import } from '../types/Ontology.ts';
import type { Axiom } from '../types/Axiom.ts';
import type { Annotation, AnnotationProperty, AnnotationValue } from '../types/Annotation.ts';
import type { ClassExpression } from '../types/ClassExpression.ts';
import type { DataRange, FacetRestriction } from '../types/DataRange.ts';
import type { ObjectPropertyExpression, DataPropertyExpression, SubObjectPropertyExpression } from '../types/PropertyExpression.ts';
import type { Entity, Individual } from '../types/Entity.ts';
import type { IRI } from '../types/IRI.ts';
import type { Literal } from '../types/Literal.ts';

import { fullIRI, abbreviatedIRI } from '../types/IRI.ts';
import { typedLiteral, plainLiteral } from '../types/Literal.ts';
import { namedIndividual, anonymousIndividual } from '../types/Entity.ts';
import * as factories from '../factories/index.ts';
import { PrefixManager } from './PrefixManager.ts';

// =============================================================================
// Parser Options
// =============================================================================

const PARSER_OPTIONS = {
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    textNodeName: '#text',
    trimValues: false,
    parseAttributeValue: false,
    isArray: (name: string) => {
        // Elements that can appear multiple times
        return [
            'Prefix', 'Import', 'Annotation',
            'Declaration', 'SubClassOf', 'EquivalentClasses', 'DisjointClasses', 'DisjointUnion',
            'SubObjectPropertyOf', 'EquivalentObjectProperties', 'DisjointObjectProperties',
            'InverseObjectProperties', 'ObjectPropertyDomain', 'ObjectPropertyRange',
            'FunctionalObjectProperty', 'InverseFunctionalObjectProperty',
            'ReflexiveObjectProperty', 'IrreflexiveObjectProperty',
            'SymmetricObjectProperty', 'AsymmetricObjectProperty', 'TransitiveObjectProperty',
            'SubDataPropertyOf', 'EquivalentDataProperties', 'DisjointDataProperties',
            'DataPropertyDomain', 'DataPropertyRange', 'FunctionalDataProperty',
            'DatatypeDefinition', 'HasKey',
            'SameIndividual', 'DifferentIndividuals', 'ClassAssertion',
            'ObjectPropertyAssertion', 'NegativeObjectPropertyAssertion',
            'DataPropertyAssertion', 'NegativeDataPropertyAssertion',
            'AnnotationAssertion', 'SubAnnotationPropertyOf',
            'AnnotationPropertyDomain', 'AnnotationPropertyRange',
            'Class', 'ObjectProperty', 'DataProperty', 'AnnotationProperty',
            'NamedIndividual', 'Datatype', 'Literal', 'FacetRestriction'
        ].includes(name);
    }
};

// =============================================================================
// Parser Class
// =============================================================================

/**
 * Parser for OWL2/XML format.
 */
export class Owl2XmlParser {
    private parser: XMLParser;
    private prefixManager: PrefixManager;

    constructor() {
        this.parser = new XMLParser(PARSER_OPTIONS);
        this.prefixManager = new PrefixManager(true);
    }

    /**
     * Parse an OWL2/XML string into an Ontology.
     */
    parse(xml: string): Ontology {
        const parsed = this.parser.parse(xml);
        const ontologyElement = parsed.Ontology;

        if (!ontologyElement) {
            throw new Error('No Ontology element found in XML');
        }

        // Reset prefix manager for this parse
        this.prefixManager = new PrefixManager(true);

        // Parse prefixes first
        const prefixes = this.parsePrefixes(ontologyElement);
        for (const p of prefixes) {
            this.prefixManager.registerPrefix(p.name, p.iri);
        }

        // Parse ontology IRIs
        const ontologyIRI = this.parseOptionalIRI(ontologyElement, '@_ontologyIRI');
        const versionIRI = this.parseOptionalIRI(ontologyElement, '@_versionIRI');

        // Parse imports
        const imports = this.parseImports(ontologyElement);

        // Parse annotations
        const annotations = this.parseAnnotations(ontologyElement.Annotation);

        // Parse axioms
        const axioms = this.parseAxioms(ontologyElement);

        return {
            ...(ontologyIRI && { ontologyIRI }),
            ...(versionIRI && { versionIRI }),
            prefixes,
            imports,
            annotations,
            axioms
        };
    }

    // =========================================================================
    // IRI Parsing
    // =========================================================================

    private parseIRI(element: any): IRI {
        if (element['@_IRI']) {
            return fullIRI(element['@_IRI']);
        }
        if (element['@_abbreviatedIRI']) {
            const parts = element['@_abbreviatedIRI'].split(':');
            if (parts.length === 2) {
                return abbreviatedIRI(parts[0], parts[1]);
            }
            return abbreviatedIRI('', element['@_abbreviatedIRI']);
        }
        throw new Error('Element has no IRI or abbreviatedIRI attribute');
    }

    private parseOptionalIRI(element: any, attr: string): IRI | undefined {
        const value = element[attr];
        if (!value) return undefined;
        return fullIRI(value);
    }

    // =========================================================================
    // Prefix and Import Parsing
    // =========================================================================

    private parsePrefixes(ontology: any): PrefixDeclaration[] {
        const prefixElements = ontology.Prefix || [];
        return prefixElements.map((p: any) => ({
            name: p['@_name'] || '',
            iri: p['@_IRI'] || ''
        }));
    }

    private parseImports(ontology: any): Import[] {
        const importElements = ontology.Import || [];
        return importElements.map((i: any) => ({
            iri: fullIRI(typeof i === 'string' ? i : i['#text'] || i['@_IRI'] || '')
        }));
    }

    // =========================================================================
    // Annotation Parsing
    // =========================================================================

    private parseAnnotations(annotations: any[]): Annotation[] {
        if (!annotations) return [];
        return annotations.map(ann => this.parseAnnotation(ann));
    }

    private parseAnnotation(ann: any): Annotation {
        const property = this.parseAnnotationProperty(ann.AnnotationProperty);
        const value = this.parseAnnotationValue(ann);
        const nestedAnnotations = this.parseAnnotations(ann.Annotation);

        return {
            property,
            value,
            ...(nestedAnnotations.length > 0 && { annotations: nestedAnnotations })
        };
    }

    private parseAnnotationProperty(element: any): AnnotationProperty {
        return {
            type: 'AnnotationProperty',
            iri: this.parseIRI(element)
        };
    }

    private parseAnnotationValue(ann: any): AnnotationValue {
        if (ann.Literal) {
            return this.parseLiteral(ann.Literal);
        }
        if (ann.IRI) {
            return fullIRI(typeof ann.IRI === 'string' ? ann.IRI : ann.IRI['#text'] || '');
        }
        if (ann.AbbreviatedIRI) {
            const parts = ann.AbbreviatedIRI.split(':');
            return abbreviatedIRI(parts[0] || '', parts[1] || '');
        }
        if (ann.AnonymousIndividual) {
            return anonymousIndividual(ann.AnonymousIndividual['@_nodeID']);
        }
        throw new Error('Unknown annotation value type');
    }

    // =========================================================================
    // Literal Parsing
    // =========================================================================

    private parseLiteral(lit: any): Literal {
        const text = typeof lit === 'string' ? lit : lit['#text'] || '';
        const datatypeIRI = lit['@_datatypeIRI'];
        const lang = lit['@_xml:lang'];

        if (datatypeIRI) {
            return typedLiteral(text, fullIRI(datatypeIRI));
        }
        return plainLiteral(text, lang);
    }

    // =========================================================================
    // Entity Parsing
    // =========================================================================

    private parseEntity(element: any, type: string): Entity {
        const iri = this.parseIRI(element);
        switch (type) {
            case 'Class':
                return factories.classEntity(iri);
            case 'Datatype':
                return factories.datatypeEntity(iri);
            case 'ObjectProperty':
                return factories.objectPropertyEntity(iri);
            case 'DataProperty':
                return factories.dataPropertyEntity(iri);
            case 'AnnotationProperty':
                return factories.annotationPropertyEntity(iri);
            case 'NamedIndividual':
                return factories.namedIndividualEntity(iri);
            default:
                throw new Error(`Unknown entity type: ${type}`);
        }
    }

    private parseIndividual(element: any): Individual {
        if (element.NamedIndividual) {
            return namedIndividual(this.parseIRI(element.NamedIndividual));
        }
        if (element.AnonymousIndividual) {
            return anonymousIndividual(element.AnonymousIndividual['@_nodeID']);
        }
        // Direct element with IRI
        if (element['@_IRI'] || element['@_abbreviatedIRI']) {
            return namedIndividual(this.parseIRI(element));
        }
        throw new Error('Cannot parse individual');
    }

    // =========================================================================
    // Property Expression Parsing
    // =========================================================================

    private parseObjectPropertyExpression(element: any): ObjectPropertyExpression {
        if (element.ObjectProperty) {
            return factories.objectProperty(this.parseIRI(element.ObjectProperty));
        }
        if (element.ObjectInverseOf) {
            const inner = factories.objectProperty(this.parseIRI(element.ObjectInverseOf.ObjectProperty));
            return factories.objectInverseOf(inner);
        }
        // Direct element with IRI
        return factories.objectProperty(this.parseIRI(element));
    }

    private parseDataPropertyExpression(element: any): DataPropertyExpression {
        if (element.DataProperty) {
            return factories.dataProperty(this.parseIRI(element.DataProperty));
        }
        return factories.dataProperty(this.parseIRI(element));
    }

    private parseSubObjectPropertyExpression(element: any): SubObjectPropertyExpression {
        if (element.ObjectPropertyChain) {
            const props = (element.ObjectPropertyChain.ObjectProperty || [])
                .map((p: any) => this.parseObjectPropertyExpression({ ObjectProperty: p }));
            if (props.length >= 2) {
                return factories.objectPropertyChain(props[0], props[1], ...props.slice(2));
            }
        }
        return this.parseObjectPropertyExpression(element);
    }

    // =========================================================================
    // Class Expression Parsing
    // =========================================================================

    private parseClassExpression(element: any): ClassExpression {
        if (element.Class) {
            return factories.owlClass(this.parseIRI(element.Class));
        }

        if (element.ObjectIntersectionOf) {
            const operands = this.parseClassExpressionList(element.ObjectIntersectionOf);
            return factories.objectIntersectionOf(operands[0], operands[1], ...operands.slice(2));
        }

        if (element.ObjectUnionOf) {
            const operands = this.parseClassExpressionList(element.ObjectUnionOf);
            return factories.objectUnionOf(operands[0], operands[1], ...operands.slice(2));
        }

        if (element.ObjectComplementOf) {
            return factories.objectComplementOf(this.parseClassExpression(element.ObjectComplementOf));
        }

        if (element.ObjectOneOf) {
            const individuals = this.parseIndividualList(element.ObjectOneOf);
            return factories.objectOneOf(individuals[0], ...individuals.slice(1));
        }

        if (element.ObjectSomeValuesFrom) {
            const e = element.ObjectSomeValuesFrom;
            return factories.objectSomeValuesFrom(
                this.parseObjectPropertyExpression(e),
                this.parseClassExpression(e)
            );
        }

        if (element.ObjectAllValuesFrom) {
            const e = element.ObjectAllValuesFrom;
            return factories.objectAllValuesFrom(
                this.parseObjectPropertyExpression(e),
                this.parseClassExpression(e)
            );
        }

        if (element.ObjectHasValue) {
            const e = element.ObjectHasValue;
            return factories.objectHasValue(
                this.parseObjectPropertyExpression(e),
                this.parseIndividual(e)
            );
        }

        if (element.ObjectHasSelf) {
            return factories.objectHasSelf(
                this.parseObjectPropertyExpression(element.ObjectHasSelf)
            );
        }

        if (element.ObjectMinCardinality) {
            const e = element.ObjectMinCardinality;
            const cardinality = parseInt(e['@_cardinality'], 10);
            const filler = this.tryParseClassExpression(e);
            return factories.objectMinCardinality(
                cardinality,
                this.parseObjectPropertyExpression(e),
                filler
            );
        }

        if (element.ObjectMaxCardinality) {
            const e = element.ObjectMaxCardinality;
            const cardinality = parseInt(e['@_cardinality'], 10);
            const filler = this.tryParseClassExpression(e);
            return factories.objectMaxCardinality(
                cardinality,
                this.parseObjectPropertyExpression(e),
                filler
            );
        }

        if (element.ObjectExactCardinality) {
            const e = element.ObjectExactCardinality;
            const cardinality = parseInt(e['@_cardinality'], 10);
            const filler = this.tryParseClassExpression(e);
            return factories.objectExactCardinality(
                cardinality,
                this.parseObjectPropertyExpression(e),
                filler
            );
        }

        // Data property restrictions
        if (element.DataSomeValuesFrom) {
            const e = element.DataSomeValuesFrom;
            const props = this.parseDataPropertyList(e);
            return factories.dataSomeValuesFrom(
                [props[0], ...props.slice(1)],
                this.parseDataRange(e)
            );
        }

        if (element.DataAllValuesFrom) {
            const e = element.DataAllValuesFrom;
            const props = this.parseDataPropertyList(e);
            return factories.dataAllValuesFrom(
                [props[0], ...props.slice(1)],
                this.parseDataRange(e)
            );
        }

        if (element.DataHasValue) {
            const e = element.DataHasValue;
            return factories.dataHasValue(
                this.parseDataPropertyExpression(e),
                this.parseLiteral(e.Literal)
            );
        }

        if (element.DataMinCardinality) {
            const e = element.DataMinCardinality;
            const cardinality = parseInt(e['@_cardinality'], 10);
            const dataRange = this.tryParseDataRange(e);
            return factories.dataMinCardinality(
                cardinality,
                this.parseDataPropertyExpression(e),
                dataRange
            );
        }

        if (element.DataMaxCardinality) {
            const e = element.DataMaxCardinality;
            const cardinality = parseInt(e['@_cardinality'], 10);
            const dataRange = this.tryParseDataRange(e);
            return factories.dataMaxCardinality(
                cardinality,
                this.parseDataPropertyExpression(e),
                dataRange
            );
        }

        if (element.DataExactCardinality) {
            const e = element.DataExactCardinality;
            const cardinality = parseInt(e['@_cardinality'], 10);
            const dataRange = this.tryParseDataRange(e);
            return factories.dataExactCardinality(
                cardinality,
                this.parseDataPropertyExpression(e),
                dataRange
            );
        }

        // Direct class reference
        if (element['@_IRI'] || element['@_abbreviatedIRI']) {
            return factories.owlClass(this.parseIRI(element));
        }

        throw new Error('Unknown class expression type');
    }

    private parseClassExpressionList(element: any): ClassExpression[] {
        const expressions: ClassExpression[] = [];
        for (const key of Object.keys(element)) {
            if (key.startsWith('@_')) continue;
            const items = Array.isArray(element[key]) ? element[key] : [element[key]];
            for (const item of items) {
                expressions.push(this.parseClassExpression({ [key]: item }));
            }
        }
        return expressions;
    }

    private tryParseClassExpression(element: any): ClassExpression | undefined {
        try {
            // Look for a class expression child
            for (const key of ['Class', 'ObjectIntersectionOf', 'ObjectUnionOf',
                              'ObjectComplementOf', 'ObjectOneOf']) {
                if (element[key]) {
                    return this.parseClassExpression({ [key]: element[key] });
                }
            }
            return undefined;
        } catch {
            return undefined;
        }
    }

    // =========================================================================
    // Data Range Parsing
    // =========================================================================

    private parseDataRange(element: any): DataRange {
        if (element.Datatype) {
            return factories.datatype(this.parseIRI(element.Datatype));
        }

        if (element.DataIntersectionOf) {
            const ranges = this.parseDataRangeList(element.DataIntersectionOf);
            return factories.dataIntersectionOf(ranges[0], ranges[1], ...ranges.slice(2));
        }

        if (element.DataUnionOf) {
            const ranges = this.parseDataRangeList(element.DataUnionOf);
            return factories.dataUnionOf(ranges[0], ranges[1], ...ranges.slice(2));
        }

        if (element.DataComplementOf) {
            return factories.dataComplementOf(this.parseDataRange(element.DataComplementOf));
        }

        if (element.DataOneOf) {
            const literals = (element.DataOneOf.Literal || []).map((l: any) => this.parseLiteral(l));
            return factories.dataOneOf(literals[0], ...literals.slice(1));
        }

        if (element.DatatypeRestriction) {
            const e = element.DatatypeRestriction;
            const dt = factories.datatype(this.parseIRI(e.Datatype));
            const facets = (e.FacetRestriction || []).map((f: any) => this.parseFacetRestriction(f));
            return factories.datatypeRestriction(dt, facets[0], ...facets.slice(1));
        }

        throw new Error('Unknown data range type');
    }

    private parseDataRangeList(element: any): DataRange[] {
        const ranges: DataRange[] = [];
        for (const key of Object.keys(element)) {
            if (key.startsWith('@_')) continue;
            const items = Array.isArray(element[key]) ? element[key] : [element[key]];
            for (const item of items) {
                ranges.push(this.parseDataRange({ [key]: item }));
            }
        }
        return ranges;
    }

    private tryParseDataRange(element: any): DataRange | undefined {
        try {
            for (const key of ['Datatype', 'DataIntersectionOf', 'DataUnionOf',
                              'DataComplementOf', 'DataOneOf', 'DatatypeRestriction']) {
                if (element[key]) {
                    return this.parseDataRange({ [key]: element[key] });
                }
            }
            return undefined;
        } catch {
            return undefined;
        }
    }

    private parseFacetRestriction(element: any): FacetRestriction {
        return factories.facetRestriction(
            fullIRI(element['@_facet']),
            this.parseLiteral(element.Literal)
        );
    }

    // =========================================================================
    // Helper Methods
    // =========================================================================

    private parseIndividualList(element: any): Individual[] {
        const individuals: Individual[] = [];
        if (element.NamedIndividual) {
            const items = Array.isArray(element.NamedIndividual)
                ? element.NamedIndividual
                : [element.NamedIndividual];
            for (const item of items) {
                individuals.push(namedIndividual(this.parseIRI(item)));
            }
        }
        if (element.AnonymousIndividual) {
            const items = Array.isArray(element.AnonymousIndividual)
                ? element.AnonymousIndividual
                : [element.AnonymousIndividual];
            for (const item of items) {
                individuals.push(anonymousIndividual(item['@_nodeID']));
            }
        }
        return individuals;
    }

    private parseDataPropertyList(element: any): DataPropertyExpression[] {
        const props: DataPropertyExpression[] = [];
        if (element.DataProperty) {
            const items = Array.isArray(element.DataProperty)
                ? element.DataProperty
                : [element.DataProperty];
            for (const item of items) {
                props.push(factories.dataProperty(this.parseIRI(item)));
            }
        }
        return props;
    }

    // =========================================================================
    // Axiom Parsing
    // =========================================================================

    private parseAxioms(ontology: any): Axiom[] {
        const axioms: Axiom[] = [];

        // Parse all axiom types
        this.parseAxiomType(ontology, 'Declaration', axioms, this.parseDeclaration.bind(this));
        this.parseAxiomType(ontology, 'SubClassOf', axioms, this.parseSubClassOf.bind(this));
        this.parseAxiomType(ontology, 'EquivalentClasses', axioms, this.parseEquivalentClasses.bind(this));
        this.parseAxiomType(ontology, 'DisjointClasses', axioms, this.parseDisjointClasses.bind(this));
        this.parseAxiomType(ontology, 'DisjointUnion', axioms, this.parseDisjointUnion.bind(this));

        // Object property axioms
        this.parseAxiomType(ontology, 'SubObjectPropertyOf', axioms, this.parseSubObjectPropertyOf.bind(this));
        this.parseAxiomType(ontology, 'EquivalentObjectProperties', axioms, this.parseEquivalentObjectProperties.bind(this));
        this.parseAxiomType(ontology, 'DisjointObjectProperties', axioms, this.parseDisjointObjectProperties.bind(this));
        this.parseAxiomType(ontology, 'InverseObjectProperties', axioms, this.parseInverseObjectProperties.bind(this));
        this.parseAxiomType(ontology, 'ObjectPropertyDomain', axioms, this.parseObjectPropertyDomain.bind(this));
        this.parseAxiomType(ontology, 'ObjectPropertyRange', axioms, this.parseObjectPropertyRange.bind(this));
        this.parseAxiomType(ontology, 'FunctionalObjectProperty', axioms, this.parseFunctionalObjectProperty.bind(this));
        this.parseAxiomType(ontology, 'InverseFunctionalObjectProperty', axioms, this.parseInverseFunctionalObjectProperty.bind(this));
        this.parseAxiomType(ontology, 'ReflexiveObjectProperty', axioms, this.parseReflexiveObjectProperty.bind(this));
        this.parseAxiomType(ontology, 'IrreflexiveObjectProperty', axioms, this.parseIrreflexiveObjectProperty.bind(this));
        this.parseAxiomType(ontology, 'SymmetricObjectProperty', axioms, this.parseSymmetricObjectProperty.bind(this));
        this.parseAxiomType(ontology, 'AsymmetricObjectProperty', axioms, this.parseAsymmetricObjectProperty.bind(this));
        this.parseAxiomType(ontology, 'TransitiveObjectProperty', axioms, this.parseTransitiveObjectProperty.bind(this));

        // Data property axioms
        this.parseAxiomType(ontology, 'SubDataPropertyOf', axioms, this.parseSubDataPropertyOf.bind(this));
        this.parseAxiomType(ontology, 'EquivalentDataProperties', axioms, this.parseEquivalentDataProperties.bind(this));
        this.parseAxiomType(ontology, 'DisjointDataProperties', axioms, this.parseDisjointDataProperties.bind(this));
        this.parseAxiomType(ontology, 'DataPropertyDomain', axioms, this.parseDataPropertyDomain.bind(this));
        this.parseAxiomType(ontology, 'DataPropertyRange', axioms, this.parseDataPropertyRange.bind(this));
        this.parseAxiomType(ontology, 'FunctionalDataProperty', axioms, this.parseFunctionalDataProperty.bind(this));

        // Other axioms
        this.parseAxiomType(ontology, 'DatatypeDefinition', axioms, this.parseDatatypeDefinition.bind(this));
        this.parseAxiomType(ontology, 'HasKey', axioms, this.parseHasKey.bind(this));

        // Assertions
        this.parseAxiomType(ontology, 'SameIndividual', axioms, this.parseSameIndividual.bind(this));
        this.parseAxiomType(ontology, 'DifferentIndividuals', axioms, this.parseDifferentIndividuals.bind(this));
        this.parseAxiomType(ontology, 'ClassAssertion', axioms, this.parseClassAssertion.bind(this));
        this.parseAxiomType(ontology, 'ObjectPropertyAssertion', axioms, this.parseObjectPropertyAssertion.bind(this));
        this.parseAxiomType(ontology, 'NegativeObjectPropertyAssertion', axioms, this.parseNegativeObjectPropertyAssertion.bind(this));
        this.parseAxiomType(ontology, 'DataPropertyAssertion', axioms, this.parseDataPropertyAssertion.bind(this));
        this.parseAxiomType(ontology, 'NegativeDataPropertyAssertion', axioms, this.parseNegativeDataPropertyAssertion.bind(this));

        // Annotation axioms
        this.parseAxiomType(ontology, 'AnnotationAssertion', axioms, this.parseAnnotationAssertion.bind(this));
        this.parseAxiomType(ontology, 'SubAnnotationPropertyOf', axioms, this.parseSubAnnotationPropertyOf.bind(this));
        this.parseAxiomType(ontology, 'AnnotationPropertyDomain', axioms, this.parseAnnotationPropertyDomain.bind(this));
        this.parseAxiomType(ontology, 'AnnotationPropertyRange', axioms, this.parseAnnotationPropertyRange.bind(this));

        return axioms;
    }

    private parseAxiomType<T extends Axiom>(
        ontology: any,
        elementName: string,
        axioms: Axiom[],
        parser: (element: any) => T
    ): void {
        const elements = ontology[elementName];
        if (!elements) return;

        const items = Array.isArray(elements) ? elements : [elements];
        for (const item of items) {
            try {
                axioms.push(parser(item));
            } catch (e) {
                console.warn(`Failed to parse ${elementName}:`, e);
            }
        }
    }

    // Individual axiom parsers
    private parseDeclaration(e: any): Axiom {
        const entityTypes = ['Class', 'Datatype', 'ObjectProperty', 'DataProperty',
                           'AnnotationProperty', 'NamedIndividual'];
        for (const type of entityTypes) {
            if (e[type]) {
                return factories.declaration(this.parseEntity(e[type], type));
            }
        }
        throw new Error('Declaration has no entity');
    }

    private parseSubClassOf(e: any): Axiom {
        const ces = this.parseClassExpressionList(e);
        return factories.subClassOf(ces[0], ces[1], this.parseAnnotations(e.Annotation));
    }

    private parseEquivalentClasses(e: any): Axiom {
        const ces = this.parseClassExpressionList(e);
        return factories.equivalentClasses(ces[0], ces[1], ...ces.slice(2));
    }

    private parseDisjointClasses(e: any): Axiom {
        const ces = this.parseClassExpressionList(e);
        return factories.disjointClasses(ces[0], ces[1], ...ces.slice(2));
    }

    private parseDisjointUnion(e: any): Axiom {
        const ces = this.parseClassExpressionList(e);
        const classRef = factories.owlClass(this.parseIRI(e.Class));
        return factories.disjointUnion(classRef, ces[0], ces[1], ...ces.slice(2));
    }

    private parseSubObjectPropertyOf(e: any): Axiom {
        const sub = this.parseSubObjectPropertyExpression(e);
        let superProp: ObjectPropertyExpression;
        if (e.ObjectProperty) {
            const items = Array.isArray(e.ObjectProperty) ? e.ObjectProperty : [e.ObjectProperty];
            superProp = this.parseObjectPropertyExpression({ ObjectProperty: items[items.length - 1] });
        } else {
            superProp = this.parseObjectPropertyExpression(e);
        }
        return factories.subObjectPropertyOf(sub, superProp, this.parseAnnotations(e.Annotation));
    }

    private parseEquivalentObjectProperties(e: any): Axiom {
        const props = (e.ObjectProperty || []).map((p: any) =>
            this.parseObjectPropertyExpression({ ObjectProperty: p }));
        return factories.equivalentObjectProperties(props[0], props[1], ...props.slice(2));
    }

    private parseDisjointObjectProperties(e: any): Axiom {
        const props = (e.ObjectProperty || []).map((p: any) =>
            this.parseObjectPropertyExpression({ ObjectProperty: p }));
        return factories.disjointObjectProperties(props[0], props[1], ...props.slice(2));
    }

    private parseInverseObjectProperties(e: any): Axiom {
        const props = (e.ObjectProperty || []).map((p: any) =>
            this.parseObjectPropertyExpression({ ObjectProperty: p }));
        return factories.inverseObjectProperties(props[0], props[1], this.parseAnnotations(e.Annotation));
    }

    private parseObjectPropertyDomain(e: any): Axiom {
        return factories.objectPropertyDomainAxiom(
            this.parseObjectPropertyExpression(e),
            this.parseClassExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseObjectPropertyRange(e: any): Axiom {
        return factories.objectPropertyRangeAxiom(
            this.parseObjectPropertyExpression(e),
            this.parseClassExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseFunctionalObjectProperty(e: any): Axiom {
        return factories.functionalObjectProperty(
            this.parseObjectPropertyExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseInverseFunctionalObjectProperty(e: any): Axiom {
        return factories.inverseFunctionalObjectProperty(
            this.parseObjectPropertyExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseReflexiveObjectProperty(e: any): Axiom {
        return factories.reflexiveObjectProperty(
            this.parseObjectPropertyExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseIrreflexiveObjectProperty(e: any): Axiom {
        return factories.irreflexiveObjectProperty(
            this.parseObjectPropertyExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseSymmetricObjectProperty(e: any): Axiom {
        return factories.symmetricObjectProperty(
            this.parseObjectPropertyExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseAsymmetricObjectProperty(e: any): Axiom {
        return factories.asymmetricObjectProperty(
            this.parseObjectPropertyExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseTransitiveObjectProperty(e: any): Axiom {
        return factories.transitiveObjectProperty(
            this.parseObjectPropertyExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseSubDataPropertyOf(e: any): Axiom {
        const props = (e.DataProperty || []).map((p: any) =>
            factories.dataProperty(this.parseIRI(p)));
        return factories.subDataPropertyOf(props[0], props[1], this.parseAnnotations(e.Annotation));
    }

    private parseEquivalentDataProperties(e: any): Axiom {
        const props = (e.DataProperty || []).map((p: any) =>
            factories.dataProperty(this.parseIRI(p)));
        return factories.equivalentDataProperties(props[0], props[1], ...props.slice(2));
    }

    private parseDisjointDataProperties(e: any): Axiom {
        const props = (e.DataProperty || []).map((p: any) =>
            factories.dataProperty(this.parseIRI(p)));
        return factories.disjointDataProperties(props[0], props[1], ...props.slice(2));
    }

    private parseDataPropertyDomain(e: any): Axiom {
        return factories.dataPropertyDomainAxiom(
            this.parseDataPropertyExpression(e),
            this.parseClassExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseDataPropertyRange(e: any): Axiom {
        return factories.dataPropertyRangeAxiom(
            this.parseDataPropertyExpression(e),
            this.parseDataRange(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseFunctionalDataProperty(e: any): Axiom {
        return factories.functionalDataProperty(
            this.parseDataPropertyExpression(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseDatatypeDefinition(e: any): Axiom {
        const dt = factories.datatype(this.parseIRI(e.Datatype));
        const dr = this.parseDataRange(e);
        return factories.datatypeDefinition(dt, dr, this.parseAnnotations(e.Annotation));
    }

    private parseHasKey(e: any): Axiom {
        const ce = this.parseClassExpression(e);
        const objProps = (e.ObjectProperty || []).map((p: any) =>
            this.parseObjectPropertyExpression({ ObjectProperty: p }));
        const dataProps = (e.DataProperty || []).map((p: any) =>
            factories.dataProperty(this.parseIRI(p)));
        return factories.hasKey(ce, objProps, dataProps, this.parseAnnotations(e.Annotation));
    }

    private parseSameIndividual(e: any): Axiom {
        const individuals = this.parseIndividualList(e);
        return factories.sameIndividual(individuals[0], individuals[1], ...individuals.slice(2));
    }

    private parseDifferentIndividuals(e: any): Axiom {
        const individuals = this.parseIndividualList(e);
        return factories.differentIndividuals(individuals[0], individuals[1], ...individuals.slice(2));
    }

    private parseClassAssertion(e: any): Axiom {
        return factories.classAssertion(
            this.parseClassExpression(e),
            this.parseIndividual(e),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseObjectPropertyAssertion(e: any): Axiom {
        const individuals = this.parseIndividualList(e);
        return factories.objectPropertyAssertion(
            this.parseObjectPropertyExpression(e),
            individuals[0],
            individuals[1],
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseNegativeObjectPropertyAssertion(e: any): Axiom {
        const individuals = this.parseIndividualList(e);
        return factories.negativeObjectPropertyAssertion(
            this.parseObjectPropertyExpression(e),
            individuals[0],
            individuals[1],
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseDataPropertyAssertion(e: any): Axiom {
        const individual = this.parseIndividual(e);
        return factories.dataPropertyAssertion(
            this.parseDataPropertyExpression(e),
            individual,
            this.parseLiteral(e.Literal),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseNegativeDataPropertyAssertion(e: any): Axiom {
        const individual = this.parseIndividual(e);
        return factories.negativeDataPropertyAssertion(
            this.parseDataPropertyExpression(e),
            individual,
            this.parseLiteral(e.Literal),
            this.parseAnnotations(e.Annotation)
        );
    }

    private parseAnnotationAssertion(e: any): Axiom {
        const property = this.parseAnnotationProperty(e.AnnotationProperty);
        let subject: IRI | { type: 'AnonymousIndividual'; nodeId: string };
        if (e.IRI) {
            subject = fullIRI(typeof e.IRI === 'string' ? e.IRI : e.IRI['#text'] || '');
        } else if (e.AbbreviatedIRI) {
            const parts = (typeof e.AbbreviatedIRI === 'string' ? e.AbbreviatedIRI : '').split(':');
            subject = abbreviatedIRI(parts[0] || '', parts[1] || '');
        } else if (e.AnonymousIndividual) {
            subject = anonymousIndividual(e.AnonymousIndividual['@_nodeID']);
        } else {
            throw new Error('AnnotationAssertion has no subject');
        }
        const value = this.parseAnnotationValue(e);
        return factories.annotationAssertion(property, subject, value, this.parseAnnotations(e.Annotation));
    }

    private parseSubAnnotationPropertyOf(e: any): Axiom {
        const props = (e.AnnotationProperty || []).map((p: any) =>
            this.parseAnnotationProperty(p));
        return factories.subAnnotationPropertyOf(props[0], props[1], this.parseAnnotations(e.Annotation));
    }

    private parseAnnotationPropertyDomain(e: any): Axiom {
        const property = this.parseAnnotationProperty(e.AnnotationProperty);
        const domain = fullIRI(typeof e.IRI === 'string' ? e.IRI : e.IRI['#text'] || '');
        return factories.annotationPropertyDomainAxiom(property, domain, this.parseAnnotations(e.Annotation));
    }

    private parseAnnotationPropertyRange(e: any): Axiom {
        const property = this.parseAnnotationProperty(e.AnnotationProperty);
        const range = fullIRI(typeof e.IRI === 'string' ? e.IRI : e.IRI['#text'] || '');
        return factories.annotationPropertyRangeAxiom(property, range, this.parseAnnotations(e.Annotation));
    }
}

/**
 * Convenience function to parse OWL2/XML string to Ontology.
 */
export function parseOntology(xml: string): Ontology {
    const parser = new Owl2XmlParser();
    return parser.parse(xml);
}
